import { Hero } from '../components/Hero';
import { InfoCard } from '../components/InfoCard';
import { FaqItem } from '../components/FaqItem';
import { LocationTab } from '../components/LocationTab';
import { Reveal } from '../components/Reveal';

const REASONS = [
  {
    title: 'Samen sporten',
    description:
      'Wij geloven dat er door samen sporten verbinding ontstaat. Kijk op de pagina van jouw dorp welke sporten er zijn in jouw buurt.',
    imageSrc: '/images/card-2.jpg',
  },
  {
    title: 'Verbinding met elkaar',
    description:
      'Bij Restart kan de jeugd elkaar ontmoeten en samen plezier maken. Als team investeren we in de jeugd — we willen een plek van vreugde en vrede zijn in het dorp.',
    imageSrc: '/images/card-3.jpg',
  },
  {
    title: "Ontdek Jezus' liefde",
    description:
      'Wij houden van God en geloven dat Hij ook van jou houdt. We geloven dat Hij graag een relatie met jou wil. Dat willen we samen met je ontdekken — we staan open voor gesprek en gebed.',
    imageSrc: '/images/card-1.jpg',
  },
];

export const HomePage = () => {
  return (
    <>
      <Hero
        title="Sportcommunity Restart"
        description="We vertellen elke zondag aan de kinderen dat we er zijn om drie redenen: omdat we houden van God, sport en mensen."
        imageSrc="/images/hero-home.jpg"
        overlay={false}
        fullHeight
        ctaLabel="Ontdek meer"
        ctaHref="#info"
        eyebrow="Sportcommunity · Ten Boer & Bedum"
      />

      {/* Info Section — editorial zig-zag, not a 3-card grid */}
      <section id="info" className="py-28 md:py-36 px-6 sm:px-10 md:px-16 bg-[#FBFAF6]">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20 md:mb-28">
            <div className="md:col-span-5">
              <span className="eyebrow text-[#0D0A00]/60">Waarom Restart</span>
            </div>
            <div className="md:col-span-7">
              <h2 className="text-[#0D0A00] text-[40px] md:text-[56px] lg:text-[64px] font-medium tracking-[-0.035em] leading-[1.02]">
                Drie redenen <br className="hidden md:block" />
                <span className="font-serif-display italic font-normal text-[#0D0A00]/70">om mee te doen.</span>
              </h2>
            </div>
          </div>

          <div className="flex flex-col gap-24 md:gap-32">
            {REASONS.map((item, i) => (
              <Reveal key={item.title}>
                <InfoCard {...item} index={i} reverse={i % 2 === 1} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-[#0D0A00] py-28 md:py-36 px-6 sm:px-10 md:px-16 relative overflow-hidden">
        {/* Subtle accent glow — singular, low intensity */}
        <div
          className="absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full opacity-[0.08] blur-3xl pointer-events-none"
          style={{ background: '#F5B800' }}
        />
        <div className="container mx-auto max-w-6xl relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-5">
              <span className="eyebrow text-white/55">Veelgestelde vragen</span>
              <h2 className="mt-6 text-white text-[40px] md:text-[52px] font-medium tracking-[-0.03em] leading-[1.03]">
                Vragen? <br />
                <span className="font-serif-display italic font-normal text-[#F5B800]">Wij denken mee.</span>
              </h2>
              <p className="mt-6 text-white/55 text-[16.5px] leading-[1.65] max-w-[40ch]">
                Bekijk hieronder de antwoorden of stel je vraag rechtstreeks aan ons team.
              </p>
              <a
                href="/contact"
                className="btn-pill mt-10"
                style={{ background: 'transparent', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }}
              >
                Neem contact op
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6H10M10 6L6 2M10 6L6 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            </div>
            <div className="lg:col-span-7 flex flex-col">
              <FaqItem
                question="Wat moet mijn kind meenemen naar Restart?"
                answer="In principe niks. Wij hebben voetballen bij ons en zorgen voor drinken en wat lekkers."
              />
              <FaqItem
                question="Wat zijn de kosten?"
                answer="Meedoen is gratis. Er zijn geen verplichtingen bij Restart. We verwachten natuurlijk wel dat er geluisterd wordt naar onze teamleden."
              />
              <FaqItem
                question="Hoe houdt Restart contact met de ouders?"
                answer="We houden ouders op de hoogte via Instagram en de nieuwsbrief. Het leukste vinden we om jullie te ontmoeten bij het veld!"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Locations Section */}
      <section className="py-28 md:py-36 px-6 sm:px-10 md:px-16">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-5">
              <span className="eyebrow text-[#0D0A00]/60">Locaties</span>
            </div>
            <div className="md:col-span-7">
              <h2 className="text-[#0D0A00] text-[36px] md:text-[48px] font-medium tracking-[-0.03em] leading-[1.05]">
                Waar je ons vindt — <br className="hidden md:block" />
                <span className="font-serif-display italic font-normal text-[#0D0A00]/70">elke zondag.</span>
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5 flex flex-col divide-y divide-[#0D0A00]/8">
              <LocationTab
                name="Ten Boer"
                address="Reigerstraat / Kievitstraat"
                isActive
                href="/ten-boer"
              />
              <LocationTab
                name="Bedum"
                address="De Kastanje 37"
                href="/bedum"
              />
            </div>
            <div className="lg:col-span-7 h-[440px] md:h-[520px] rounded-[20px] overflow-hidden border border-[#0D0A00]/8 shadow-[0_20px_50px_-20px_rgba(13,10,0,0.18)]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d792.5469967670009!2d6.6880171!3d53.2763402!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c9d34e151260eb%3A0x8ac9345f9f1fe0db!2sSportcommunity%20Restart!5e1!3m2!1snl!2snl!4v1749199215061!5m2!1snl!2snl"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Locaties kaart"
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};
